import { DocumentContent } from "../utils/wordContextExtractor";
import { PipelinePayload, PipelineResponse, SubmitCommentPayload, SubmitCommentResponse } from "./types";

export function getPipelineUrl(): string {
  const value = (process.env as Record<string, string | undefined>)["RUN_PIPELINE_URL"];
  if (!value || value === "undefined") {
    throw new Error(
      "Missing configuration for RUN_PIPELINE_URL. Please check your .env file and rebuild the project."
    );
  }
  return value;
}

export function getSubmitCommentUrl(): string {
  const baseUrl = getPipelineUrl().replace('/run_pipeline', '');
  return `${baseUrl}/submit_comment`;
}

export async function submitComment(
  comment: string,
  action: string,
  userInput: string,
  section: string,
  threadId?: string
): Promise<SubmitCommentResponse> {
  const endpoint = getSubmitCommentUrl();
  
  const payload: SubmitCommentPayload = {
    comment,
    action: action.toUpperCase(),
    userInput,
    section,
    thread_id: threadId
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "<no body>");
      const errorMsg = `HTTP ${response.status} ${response.statusText} - ${errorText}`;
      console.error("❌ Comment submission error:", errorMsg);
      throw new Error(errorMsg);
    }

    const result = await response.json() as SubmitCommentResponse;
    console.log(`✅ Comment submitted successfully:`, result);
    return result;
  } catch (error) {
    console.error("❌ Comment submission failed:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

export async function sendDetailedCheckToBackend(pageNumber: number, subpart: string): Promise<PipelineResponse> {
  return runPipeline(
    `Detailed check for page ${pageNumber}, subpart ${subpart}`,
    undefined,
    undefined,
    undefined,
    pageNumber,
    undefined,
    subpart
  );
}

export async function sendExportSummaryToBackend(): Promise<PipelineResponse> {
  return runPipeline("Export summary request");
}

export async function runPipeline(
  query: string,
  document?: DocumentContent,
  documentId?: string,
  documentPreview?: string,
  pageNumber?: number,
  pages?: number[],
  subpart?: string,
  totalPages?: number,
  triggerFromFrontend?: boolean
): Promise<PipelineResponse> {
  const endpoint = getPipelineUrl();

  const payload: PipelinePayload = {
    query,
    document,
    documentId,
    documentPreview,
    pageNumber,
    pages,
    subpart,
    totalPages,
    timestamp: new Date().toISOString(),
    triggerFromFrontend,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "<no body>");
      const errorMsg = `HTTP ${response.status} ${response.statusText} - ${errorText}`;
      console.error("Pipeline error response:", errorMsg);
      throw new Error(errorMsg);
    }

    const parsedRaw = await response.json().catch(() => ({}));
    console.log("🔍 Raw backend comments_data:", parsedRaw.comments_data);

    // Normalize a few possible backend response shapes into PipelineResponse
    const anyParsed = parsedRaw as any;
    const normalized: PipelineResponse = {};

    if (typeof anyParsed === "string") {
      normalized.response = anyParsed;
      normalized.success = true;
    } else {
      console.log("🔍 anyParsed.comments_data before assignment:", anyParsed.comments_data);
      
      // Common locations where a textual assistant reply might appear
      normalized.response =
        anyParsed.response ??
        anyParsed.final_result?.last_tool_result ??
        anyParsed.final_result?.message ??
        anyParsed.final_result?.user_input ??
        anyParsed.final_result ??
        (anyParsed.messages && anyParsed.messages[0]?.content) ??
        (anyParsed.value && anyParsed.value[0]?.content) ??
        undefined;

      // If no response found, check for compliance_results and stringify the whole object
      if (!normalized.response && anyParsed.compliance_results) {
        try {
          normalized.response = JSON.stringify(anyParsed);
        } catch {
          normalized.response = String(anyParsed);
        }
      }

      normalized.success = anyParsed.success ?? true;
      normalized.document_summary = anyParsed.document_summary;
      normalized.comments_data = anyParsed.comments_data;
      normalized.total_comments = anyParsed.total_comments;
      normalized.message = anyParsed.message;
      
      console.log("🔍 After assignment - normalized keys:", Object.keys(normalized));

      // If response is an object, stringify a helpful fallback
      if (normalized.response && typeof normalized.response !== "string") {
        try {
          normalized.response = JSON.stringify(normalized.response);
        } catch {
          normalized.response = String(normalized.response);
        }
      }
    }
    console.log("🔍 Normalized comments_data:", normalized.comments_data);
    return normalized;
  } catch (error) {
    console.error("Pipeline request failed:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}